import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { useCopilotAction, useCopilotReadable } from '@copilotkit/react-core';

import { appNavigation } from '@/app/navigation';
import { toLocalDateTimeInput } from '@/lib/assistant-draft-utils';
import { useAssistantStore } from '@/state/assistant-store';
import { selectActiveRole, useSessionStore } from '@/state/session-store';

export function AssistantBridge() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeRole = useSessionStore(selectActiveRole);
  const { memberships, activeOrganizationId, profile, user } = useSessionStore(
    useShallow((state) => ({
      memberships: state.memberships,
      activeOrganizationId: state.activeOrganizationId,
      profile: state.profile,
      user: state.user,
    })),
  );
  const { setCurrentRoute, setNoteDraft, setTaskDraft, setEventDraft, setBriefingDraft, setAssistantOpen } =
    useAssistantStore(
      useShallow((state) => ({
        setCurrentRoute: state.setCurrentRoute,
        setNoteDraft: state.setNoteDraft,
        setTaskDraft: state.setTaskDraft,
        setEventDraft: state.setEventDraft,
        setBriefingDraft: state.setBriefingDraft,
        setAssistantOpen: state.setAssistantOpen,
      })),
    );

  const activeOrganization = useMemo(
    () => memberships.find((membership) => membership.organization_id === activeOrganizationId)?.organization ?? null,
    [activeOrganizationId, memberships],
  );

  const currentWorkspace = useMemo(
    () => appNavigation.find((item) => location.pathname.startsWith(item.to)) ?? null,
    [location.pathname],
  );

  useEffect(() => {
    setCurrentRoute(location.pathname);
  }, [location.pathname, setCurrentRoute]);

  useCopilotReadable({
    description: 'Authenticated NordicCompanyHub user context',
    value: {
      userId: user?.id ?? null,
      fullName: profile?.full_name ?? null,
      title: profile?.title ?? null,
      activeRole,
    },
  });

  useCopilotReadable({
    description: 'Active organization and route context for the current workspace',
    value: {
      organizationId: activeOrganizationId,
      organizationName: activeOrganization?.name ?? null,
      organizationCountry: activeOrganization?.country ?? null,
      currentRoute: location.pathname,
      currentWorkspace: currentWorkspace?.label ?? null,
    },
  });

  useCopilotReadable({
    description: 'Supported assistant behaviors in NordicCompanyHub',
    value: {
      capabilities: [
        'Answer questions about the current organization using readable app context and server-side workspace reads.',
        'Navigate between pages.',
        'Prepare drafts for notes, tasks, calendar events, and executive briefings.',
      ],
      guardrails: ['All write actions are draft-only and require the user to review and submit in the UI.'],
    },
  });

  useCopilotAction({
    name: 'openWorkspacePage',
    description: 'Open a page in the workspace navigation. Use this when the user asks to go to a specific module.',
    parameters: [
      {
        name: 'destination',
        type: 'string',
        enum: appNavigation.map((item) => item.label),
        description: 'The workspace page to open.',
        required: true,
      },
    ],
    handler: ({ destination }) => {
      const target = appNavigation.find((item) => item.label === destination);
      if (!target) {
        return `Could not find a workspace page named ${destination}.`;
      }

      navigate(target.to);
      setAssistantOpen(true);
      return `Opened ${target.label}.`;
    },
  });

  useCopilotAction({
    name: 'draftNote',
    description: 'Prepare a note draft in the Notes workspace. This only fills the editor for the user to review and save.',
    parameters: [
      { name: 'title', type: 'string', description: 'The note title.', required: true },
      { name: 'contentMarkdown', type: 'string', description: 'Markdown content for the note.', required: true },
      { name: 'isPinned', type: 'boolean', description: 'Whether the note should be pinned.', required: false },
      { name: 'companyId', type: 'string', description: 'Optional linked company id.', required: false },
    ],
    handler: ({ title, contentMarkdown, isPinned, companyId }) => {
      setNoteDraft({
        title,
        content_markdown: contentMarkdown,
        is_pinned: Boolean(isPinned),
        company_id: companyId ?? null,
      });
      navigate('/notes');
      return 'Prepared a note draft in the Notes workspace. The user still needs to save it.';
    },
  });

  useCopilotAction({
    name: 'draftTask',
    description: 'Prepare a task draft in the Tasks workspace. This only fills the task form for the user to review and save.',
    parameters: [
      { name: 'title', type: 'string', description: 'Task title.', required: true },
      { name: 'description', type: 'string', description: 'Optional task description.', required: false },
      {
        name: 'priority',
        type: 'string',
        enum: ['low', 'medium', 'high', 'critical'],
        description: 'Task priority.',
        required: false,
      },
      {
        name: 'status',
        type: 'string',
        enum: ['todo', 'in_progress', 'blocked', 'done'],
        description: 'Initial task status.',
        required: false,
      },
      { name: 'dueDate', type: 'string', description: 'Optional due date in YYYY-MM-DD format.', required: false },
      { name: 'companyId', type: 'string', description: 'Optional linked company id.', required: false },
    ],
    handler: ({ title, description, priority, status, dueDate, companyId }) => {
      setTaskDraft({
        title,
        description: description ?? '',
        priority: priority ?? 'medium',
        status: status ?? 'todo',
        due_date: dueDate ?? '',
        company_id: companyId ?? null,
      });
      navigate('/tasks');
      return 'Prepared a task draft in the Tasks workspace. The user still needs to save it.';
    },
  });

  useCopilotAction({
    name: 'draftCalendarEvent',
    description: 'Prepare a calendar event draft. This only fills the event form for the user to review and save.',
    parameters: [
      { name: 'title', type: 'string', description: 'Event title.', required: true },
      { name: 'description', type: 'string', description: 'Optional event description.', required: false },
      {
        name: 'eventType',
        type: 'string',
        enum: ['meeting', 'filing_deadline', 'legal_review', 'earnings_date', 'internal_reminder'],
        description: 'The calendar event type.',
        required: false,
      },
      { name: 'startsAt', type: 'string', description: 'Optional datetime-local string for the start time.', required: false },
      { name: 'endsAt', type: 'string', description: 'Optional datetime-local string for the end time.', required: false },
      { name: 'companyId', type: 'string', description: 'Optional linked company id.', required: false },
    ],
    handler: ({ title, description, eventType, startsAt, endsAt, companyId }) => {
      const start = startsAt ?? toLocalDateTimeInput(new Date());
      const end =
        endsAt ??
        toLocalDateTimeInput(new Date(new Date(start.replace(' ', 'T')).getTime() + 60 * 60 * 1000));

      setEventDraft({
        title,
        description,
        event_type: eventType ?? 'meeting',
        starts_at: start,
        ends_at: end,
        company_id: companyId ?? null,
      });
      navigate('/calendar');
      return 'Prepared a calendar event draft in the Calendar workspace. The user still needs to save it.';
    },
  });

  useCopilotAction({
    name: 'draftExecutiveBriefing',
    description: 'Prepare an executive briefing draft in the AI workspace. This only fills the briefing editor for review and saving.',
    parameters: [
      { name: 'title', type: 'string', description: 'Briefing title.', required: true },
      { name: 'content', type: 'string', description: 'The draft briefing content in markdown.', required: true },
      { name: 'sourcePrompt', type: 'string', description: 'Optional source prompt or request summary.', required: false },
    ],
    handler: ({ title, content, sourcePrompt }) => {
      setBriefingDraft({
        title,
        content,
        sourcePrompt,
      });
      navigate('/insights');
      return 'Prepared an executive briefing draft in the AI workspace. The user still needs to save it.';
    },
  });

  return null;
}
